import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Button,
  Input,
  Space,
  Tag,
  Popconfirm,
  message,
  Modal,
  Form,
  Switch,
  Tooltip,
  Typography,
  Avatar,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  CopyOutlined,
  ReloadOutlined,
  FormOutlined,
  PlayCircleOutlined,
  UserOutlined,
  QrcodeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import PagesWrapper from '../../PagesWrapper';
import surveysService from '../../services/surveysService';
import { getFileUrl } from '../../utils/urlUtils';
import styles from './Surveys.module.css';

const { Search } = Input;
const { Title, Text } = Typography;

const SurveysList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrSurvey, setQrSurvey] = useState(null);

  const fetchSurveys = useCallback(async (page = 1, search = searchText) => {
    setLoading(true);
    try {
      const response = await surveysService.getSurveys({
        page,
        pageSize: pagination.pageSize,
        search: search || undefined,
      });
      setSurveys(response.items || []);
      setPagination(prev => ({
        ...prev,
        current: response.page,
        total: response.totalCount,
      }));
    } catch (error) {
      message.error(t('surveys.fetchError', 'Failed to load surveys'));
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, searchText, t]);

  useEffect(() => {
    fetchSurveys();
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    fetchSurveys(1, value);
  };

  const handleTableChange = (paginationConfig) => {
    fetchSurveys(paginationConfig.current);
  };

  const handleCreate = () => {
    setEditingSurvey(null);
    form.resetFields();
    form.setFieldsValue({ allowAnonymous: true });
    setModalVisible(true);
  };

  const handleEdit = (survey) => {
    navigate(`/surveys/${survey.id}`);
  };

  const handleQuickEdit = (survey) => {
    setEditingSurvey(survey);
    form.setFieldsValue({
      name: survey.name,
      allowAnonymous: survey.allowAnonymous,
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingSurvey) {
        await surveysService.updateSurvey(editingSurvey.id, values);
        message.success(t('surveys.updateSuccess', 'Survey updated successfully'));
      } else {
        const newSurvey = await surveysService.createSurvey(values);
        message.success(t('surveys.createSuccess', 'Survey created successfully'));
        // Navigate to editor for new surveys
        navigate(`/surveys/${newSurvey.id}`);
        return;
      }

      setModalVisible(false);
      fetchSurveys(pagination.current);
    } catch (error) {
      if (error.errorFields) return; // Form validation error
      message.error(t('surveys.saveError', 'Failed to save survey'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (survey) => {
    try {
      await surveysService.deleteSurvey(survey.id);
      message.success(t('surveys.deleteSuccess', 'Survey deleted successfully'));
      fetchSurveys(pagination.current);
    } catch (error) {
      message.error(t('surveys.deleteError', 'Failed to delete survey'));
    }
  };

  const handleTogglePublish = async (survey) => {
    try {
      if (survey.isPublished) {
        await surveysService.unpublishSurvey(survey.id);
        message.success(t('surveys.unpublishSuccess', 'Survey unpublished'));
      } else {
        await surveysService.publishSurvey(survey.id);
        message.success(t('surveys.publishSuccess', 'Survey published'));
      }
      fetchSurveys(pagination.current);
    } catch (error) {
      message.error(t('surveys.togglePublishError', 'Failed to update survey status'));
    }
  };

  const handleViewStatistics = (survey) => {
    navigate(`/surveys/${survey.id}/statistics`);
  };

  const handleViewResponses = (survey) => {
    navigate(`/surveys/${survey.id}/responses`);
  };

  const handleCopyLink = (survey) => {
    const url = `${window.location.origin}/survey/${survey.id}`;
    navigator.clipboard.writeText(url);
    message.success(t('surveys.linkCopied', 'Survey link copied to clipboard'));
  };

  const handleOpenSurvey = (survey) => {
    window.open(`/survey/${survey.id}`, '_blank');
  };

  const handleShowQrCode = (survey) => {
    setQrSurvey(survey);
    setQrModalVisible(true);
  };

  const handleDownloadQrCode = () => {
    const svg = document.getElementById('survey-qr-code');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${qrSurvey?.name || 'survey'}-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const columns = [
    {
      title: t('surveys.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span className={styles.surveyName} onClick={() => handleEdit(record)}>
          {text}
        </span>
      ),
    },
    {
      title: t('surveys.questions', 'Questions'),
      dataIndex: 'questionCount',
      key: 'questionCount',
      width: 100,
      render: (count) => (
        <span className={styles.questionCount}>{count}</span>
      ),
    },
    {
      title: t('surveys.responses', 'Responses'),
      dataIndex: 'responseCount',
      key: 'responseCount',
      width: 100,
      render: (count) => (
        <span className={styles.responseCount}>{count}</span>
      ),
    },
    {
      title: t('surveys.status', 'Status'),
      dataIndex: 'isPublished',
      key: 'isPublished',
      width: 120,
      render: (isPublished) => (
        <Tag
          color={isPublished ? 'green' : 'default'}
          icon={isPublished ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          className={styles.statusTag}
        >
          {isPublished ? t('surveys.published', 'Published') : t('surveys.draft', 'Draft')}
        </Tag>
      ),
    },
    {
      title: t('surveys.anonymous', 'Anonymous'),
      dataIndex: 'allowAnonymous',
      key: 'allowAnonymous',
      width: 100,
      render: (allow) => (
        <Tag color={allow ? 'blue' : 'orange'}>
          {allow ? t('surveys.yes', 'Yes') : t('surveys.no', 'No')}
        </Tag>
      ),
    },
    {
      title: t('surveys.createdBy', 'Created By'),
      key: 'addedBy',
      width: 250,
      render: (_, record) => (
        <Space size="small">
          <Avatar 
            size="small" 
            src={getFileUrl(record.addedByProfileImageUrl)}
            icon={!record.addedByProfileImageUrl && <UserOutlined />}
          />
          <span>{record.addedByName || t('common.unknown', 'Unknown')}</span>
        </Space>
      ),
    },
    {
      title: t('surveys.actions', 'Actions'),
      key: 'actions',
      width: 320,
      render: (_, record) => (
        <Space size="small" className={styles.actions}>
          <Tooltip title={t('surveys.edit', 'Edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={t('surveys.statistics', 'Statistics')}>
            <Button
              type="text"
              icon={<BarChartOutlined />}
              onClick={() => handleViewStatistics(record)}
              disabled={record.responseCount === 0}
            />
          </Tooltip>
          <Tooltip title={t('surveys.viewResponses', 'View Responses')}>
            <Button
              type="text"
              icon={<TeamOutlined />}
              onClick={() => handleViewResponses(record)}
              disabled={record.responseCount === 0}
            />
          </Tooltip>
          <Tooltip title={record.isPublished ? t('surveys.unpublish', 'Unpublish') : t('surveys.publish', 'Publish')}>
            <Button
              type="text"
              icon={record.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleTogglePublish(record)}
            />
          </Tooltip>
          {record.isPublished && (
            <Tooltip title={t('surveys.copyLink', 'Copy Link')}>
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => handleCopyLink(record)}
              />
            </Tooltip>
          )}
          {record.isPublished && (
            <Tooltip title={t('surveys.qrCode', 'QR Code')}>
              <Button
                type="text"
                icon={<QrcodeOutlined />}
                onClick={() => handleShowQrCode(record)}
              />
            </Tooltip>
          )}
          {record.isPublished && (
            <Tooltip title={t('surveys.openSurvey', 'Open Survey')}>
              <Button
                type="text"
                icon={<PlayCircleOutlined />}
                onClick={() => handleOpenSurvey(record)}
              />
            </Tooltip>
          )}
          <Popconfirm
            title={t('surveys.deleteConfirm', 'Delete this survey?')}
            description={t('surveys.deleteDescription', 'This will permanently delete the survey and all responses.')}
            onConfirm={() => handleDelete(record)}
            okText={t('common.yes', 'Yes')}
            cancelText={t('common.no', 'No')}
            okButtonProps={{ danger: true }}
          >
            <Tooltip title={t('surveys.delete', 'Delete')}>
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleRefresh = () => {
    fetchSurveys(pagination.current, searchText);
  };

  return (
    <PagesWrapper>
      <div className={styles.surveysContainer}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <FormOutlined className={styles.titleIcon} />
            <Title level={2} className={styles.title}>
              {t('surveys.title', 'Surveys')}
            </Title>
          </div>
          <Text type="secondary">
            {t('surveys.description', 'Create and manage surveys to collect feedback and data')}
          </Text>
        </div>

        <div className={styles.toolbar}>
          <Space>
            <Search
              placeholder={t('surveys.searchPlaceholder', 'Search surveys...')}
              onSearch={handleSearch}
              allowClear
              style={{ width: 300 }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              {t('common.refresh', 'Refresh')}
            </Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('surveys.createNew', 'Create Survey')}
          </Button>
        </div>

      <Table
        columns={columns}
        dataSource={surveys}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showTotal: (total) => t('surveys.totalItems', `Total: ${total} surveys`, { total }),
        }}
        onChange={handleTableChange}
        locale={{
          emptyText: (
            <div className={styles.emptyState}>
              <p>{t('surveys.noSurveys', 'No surveys found')}</p>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                {t('surveys.createFirst', 'Create your first survey')}
              </Button>
            </div>
          ),
        }}
      />

      <Modal
        title={editingSurvey ? t('surveys.editSurvey', 'Edit Survey') : t('surveys.createSurvey', 'Create Survey')}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        okText={editingSurvey ? t('common.save', 'Save') : t('common.create', 'Create')}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={t('surveys.surveyName', 'Survey Name')}
            rules={[
              { required: true, message: t('surveys.nameRequired', 'Please enter a survey name') },
              { max: 255, message: t('surveys.nameTooLong', 'Name is too long (max 255 characters)') },
            ]}
          >
            <Input placeholder={t('surveys.namePlaceholder', 'Enter survey name...')} />
          </Form.Item>

          <Form.Item
            name="allowAnonymous"
            label={t('surveys.allowAnonymous', 'Allow Anonymous Participation')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {!editingSurvey && (
            <Text type="secondary">
              {t('surveys.createNote', 'After creating, you will be taken to the editor to add questions.')}
            </Text>
          )}
        </Form>
      </Modal>

      <Modal
        title={t('surveys.qrCode', 'QR Code')}
        open={qrModalVisible}
        onCancel={() => setQrModalVisible(false)}
        footer={[
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadQrCode}>
            {t('surveys.downloadQrCode', 'Download QR Code')}
          </Button>,
          <Button key="close" onClick={() => setQrModalVisible(false)}>
            {t('common.close', 'Close')}
          </Button>,
        ]}
        centered
      >
        {qrSurvey && (
          <div className={styles.qrCodeContainer}>
            <QRCodeSVG
              id="survey-qr-code"
              value={`${window.location.origin}/survey/${qrSurvey.id}`}
              size={256}
              level="H"
              includeMargin
            />
            <p className={styles.qrCodeSurveyName}>{qrSurvey.name}</p>
            <p className={styles.qrCodeLink}>{`${window.location.origin}/survey/${qrSurvey.id}`}</p>
          </div>
        )}
      </Modal>
      </div>
    </PagesWrapper>
  );
};

export default SurveysList;
